import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { PremiumGate } from "@/components/PremiumGate";
import axios from "axios";
import { Camera, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function CardsContent() {
  const { token } = useAuth();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);   // { src, name } da confermare
  const [nameInput, setNameInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [lightbox, setLightbox] = useState(null); // card da visualizzare
  const fileRef = useRef();

  const load = () => {
    axios.get(`${API}/api/business-cards`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setCards(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [token]);

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPreview({ src: reader.result });
      setNameInput("");
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleSave = async () => {
    if (!nameInput.trim() || !preview) return;
    setSaving(true);
    try {
      await axios.post(`${API}/api/business-cards`,
        { name: nameInput.trim(), image_data: preview.src },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Biglietto salvato");
      setPreview(null);
      setNameInput("");
      load();
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    try {
      await axios.delete(`${API}/api/business-cards/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`"${name}" eliminato`);
      setCards(c => c.filter(x => x.id !== id));
      if (lightbox?.id === id) setLightbox(null);
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-extrabold text-[#0F172A]">Biglietti da visita</h1>
          <p className="text-slate-400 text-sm">{cards.length} biglietti · ordine alfabetico</p>
        </div>
        <button
          data-testid="add-card-btn"
          onClick={() => fileRef.current?.click()}
          className="flex items-center gap-2 bg-[#0F172A] text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-slate-800 transition active:scale-95"
        >
          <Camera className="w-4 h-4" />
          <span>Scansiona</span>
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={onFileChange}
        />
      </div>

      {/* Preview dialog */}
      {preview && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-[#0F172A]">Nuovo biglietto</h3>
              <button onClick={() => setPreview(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="w-full bg-slate-50 rounded-xl flex items-center justify-center p-3" style={{ aspectRatio: '16/10' }}>
              <img src={preview.src} alt="" className="max-w-full max-h-full object-contain rounded-lg shadow-sm" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Nome (per ordinamento alfabetico)</label>
              <input
                autoFocus
                value={nameInput}
                onChange={e => setNameInput(e.target.value)}
                placeholder="Es. Mario Rossi"
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-slate-400"
                data-testid="card-name-input"
              />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setPreview(null)} className="flex-1 border border-slate-200 py-2.5 rounded-xl text-sm text-slate-600">Annulla</button>
              <button
                onClick={handleSave}
                disabled={!nameInput.trim() || saving}
                data-testid="save-card-btn"
                className="flex-1 bg-[#0F172A] text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-40"
              >
                {saving ? "Salvataggio..." : "Salva"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 bg-black/80 z-50 flex flex-col items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <img src={lightbox.image_data} alt={lightbox.name} className="max-w-full max-h-[70vh] rounded-xl object-contain" onClick={e => e.stopPropagation()} />
          <div className="mt-4 flex items-center gap-4">
            <span className="text-white font-semibold">{lightbox.name}</span>
            <button
              onClick={e => { e.stopPropagation(); handleDelete(lightbox.id, lightbox.name); }}
              className="text-red-400 hover:text-red-300 transition"
              data-testid={`delete-card-${lightbox.id}`}
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
          <p className="text-white/40 text-xs mt-2">Tocca fuori per chiudere</p>
        </div>
      )}

      {/* Gallery */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#0F172A] border-t-transparent rounded-full animate-spin" /></div>
      ) : cards.length === 0 ? (
        <div className="text-center py-16">
          <Camera className="w-12 h-12 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">Nessun biglietto ancora.<br />Scansiona il tuo primo biglietto da visita.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {cards.map(card => (
            <button
              key={card.id}
              data-testid={`card-item-${card.id}`}
              onClick={() => setLightbox(card)}
              className="group relative bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow text-left"
            >
              {/* Contenitore proporzione biglietto da visita standard (85×54mm ≈ 16:10) */}
              <div className="w-full bg-slate-50 flex items-center justify-center p-2" style={{ aspectRatio: '16/10' }}>
                <img
                  src={card.image_data}
                  alt={card.name}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-sm"
                  style={{ maxHeight: '100%', maxWidth: '100%' }}
                />
              </div>
              <div className="px-2 py-1.5 border-t border-slate-100">
                <p className="text-xs font-semibold text-[#0F172A] truncate">{card.name}</p>
              </div>
            </button>
          ))}
          <button
            onClick={() => fileRef.current?.click()}
            className="flex flex-col items-center justify-center h-36 border-2 border-dashed border-slate-200 rounded-xl text-slate-400 hover:border-slate-400 hover:text-slate-600 transition"
          >
            <Plus className="w-6 h-6 mb-1" />
            <span className="text-xs">Aggiungi</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default function BusinessCardsPage() {
  return (
    <PremiumGate>
      <CardsContent />
    </PremiumGate>
  );
}
